import React from 'react'
import ReactDOM, {unmountComponentAtNode} from 'react-dom'
import {DataObjectId, Hub} from 'yougile-model'
import {
  UserStickerProps,
  UserStickerState
} from '@extensions/sticker/views-web/user/user-sticker-state'
import {UserAccess} from '@extensions/user-access'
import {Sync} from '../../../client/sync'
import {elementRendererCreator} from '../../../components/element-render'
import {UIElement} from '../ui-element'

export interface UserSelectOptions {
  chosen: DataObjectId[]
  available: DataObjectId[]
  onChoose: (choosen: DataObjectId[]) => void
  onClose?: () => void
  multiChoice?: boolean
  maxShow?: number
  disabled?: boolean
  title?: string
  size?: number
  child?: UIElement
  showIcon?: boolean
  showEmptyIcon?: boolean
  projectId?: DataObjectId
  childrenClassName?: string
  avatarsClassName?: string
  dataId?: DataObjectId
  shift?: number
  disableShift?: boolean
}

export class UserSelect extends UIElement {
  private options: UserSelectOptions
  private previous: HTMLElement

  constructor(companyId: DataObjectId, options: UserSelectOptions) {
    super(companyId, options?.dataId, undefined)
    this.options = options
  }

  public override render(el: HTMLElement): void {
    this.clean()

    el.innerHTML = ''
    el.className = 'ui-user-select'
    renderElement(el, this.getProps(this.options))
    super.render(el)

    this.previous = el
  }

  private getProps(options: UserSelectOptions) {
    const project = getObj(options.projectId)

    const users = project
      ? UserAccess.listUsersInProject(project)
      : Sync.get({companyId: this.companyId}).getAllUsers(false)

    const usersIds = users.map(u => u.id)

    return {
      onChoose: options.onChoose,
      onClose: options.onClose,
      shift: options.shift,
      disableShift: options.disableShift,
      users: options.chosen ?? [],
      options: options.available ?? usersIds,
      disabled: options.disabled ?? false,
      singleMode: !options.multiChoice,
      maxShow: options.maxShow,
      showIcon: options.showIcon,
      showEmptyIcon: options.showEmptyIcon,
      title: options.title,
      size: options.size,
      childrenClassName: options.childrenClassName,
      avatarsClassName: options.avatarsClassName,
      children: options.child
        ? elementRendererCreator({render: el => options.child?.render(el)})
        : undefined
    }
  }

  public rerender(props?: Partial<UserSelectOptions>) {
    renderElement(this.previous, {...this.getProps(this.options), ...props})
  }

  public clean() {
    // to avoid memory leaks
    this.previous && unmountComponentAtNode(this.previous)
  }
}

export const renderElement = (
  element: HTMLElement,
  props: UserStickerProps
) => {
  ReactDOM.render(<UserStickerState {...props} />, element)
}

const getObj = (id?: DataObjectId): Hub | undefined => {
  if (!id) {
    return undefined
  }
  return Sync.getCurrentSandbox().getHub(id)
}
